import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  People as UsersIcon,
  LocationOn as LocationIcon,
  School as SchoolIcon,
  DataUsage as DataIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';

const Administration: React.FC = () => {
  const adminSections = [
    {
      title: 'User Management',
      description: 'Manage user accounts, roles, and permissions',
      icon: <UsersIcon color="primary" />,
      actions: ['View Users', 'Add User', 'Manage Roles'],
    },
    {
      title: 'Geographic Hierarchy',
      description: 'Manage regions, districts, wards, and shehias',
      icon: <LocationIcon color="secondary" />,
      actions: ['View Hierarchy', 'Add Location', 'Manage Boundaries'],
    },
    {
      title: 'School Configuration',
      description: 'Configure school types, levels, and classifications',
      icon: <SchoolIcon color="success" />,
      actions: ['School Types', 'Education Levels', 'Ownership Categories'],
    },
    {
      title: 'Data Management',
      description: 'Configure data years, validation rules, and import settings',
      icon: <DataIcon color="warning" />,
      actions: ['Data Years', 'Validation Rules', 'Import Templates'],
    },
    {
      title: 'System Settings',
      description: 'General system configuration and preferences',
      icon: <SettingsIcon color="info" />,
      actions: ['General Settings', 'Languages', 'System Preferences'],
    },
    {
      title: 'Security & Audit',
      description: 'Security settings and audit logs',
      icon: <SecurityIcon color="error" />,
      actions: ['Audit Logs', 'Security Settings', 'Access Control'],
    },
  ];

  const systemStats = [
    { label: 'Total Users', value: 25, color: 'primary' },
    { label: 'Active Sessions', value: 8, color: 'success' },
    { label: 'Data Years', value: 5, color: 'info' },
    { label: 'Import Jobs', value: 142, color: 'warning' },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        System Administration
      </Typography>

      {/* System Overview */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          System Overview
        </Typography>
        <Grid container spacing={3}>
          {systemStats.map((stat, index) => (
            <Grid item xs={6} sm={3} key={index}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color={`${stat.color}.main`}>
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.label}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Administration Sections */}
      <Grid container spacing={3}>
        {adminSections.map((section, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {section.icon}
                  <Typography variant="h6" sx={{ ml: 1 }}>
                    {section.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {section.description}
                </Typography>
                <Box sx={{ mt: 2 }}>
                  {section.actions.map((action, actionIndex) => (
                    <Chip
                      key={actionIndex}
                      label={action}
                      variant="outlined"
                      size="small"
                      sx={{ mr: 1, mb: 1 }}
                    />
                  ))}
                </Box>
              </CardContent>
              <CardActions>
                <Button size="small" variant="contained">
                  Manage
                </Button>
                <Button size="small" variant="outlined">
                  Configure
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Recent Activity */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Recent Administrative Activity
        </Typography>
        <List>
          <ListItem divider>
            <ListItemIcon>
              <UsersIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="New user account created"
              secondary="John Doe added as District Administrator - 2 hours ago"
            />
            <Chip label="User Management" size="small" variant="outlined" />
          </ListItem>
          
          <ListItem divider>
            <ListItemIcon>
              <DataIcon color="warning" />
            </ListItemIcon>
            <ListItemText
              primary="Data year configuration updated"
              secondary="Academic Year 2024 set as current - 1 day ago"
            />
            <Chip label="Data Management" size="small" variant="outlined" />
          </ListItem>
          
          <ListItem divider>
            <ListItemIcon>
              <SchoolIcon color="success" />
            </ListItemIcon>
            <ListItemText
              primary="School type added"
              secondary="Community Secondary School type created - 3 days ago"
            />
            <Chip label="School Config" size="small" variant="outlined" />
          </ListItem>
          
          <ListItem>
            <ListItemIcon>
              <SecurityIcon color="error" />
            </ListItemIcon>
            <ListItemText
              primary="Security audit completed"
              secondary="System security audit passed - 1 week ago"
            />
            <Chip label="Security" size="small" variant="outlined" />
          </ListItem>
        </List>
      </Paper>
    </Box>
  );
};

export default Administration;